/**
 * Axios API Client Configuration
 *
 * This module provides a pre-configured Axios instance for making API calls
 * to the Django backend with proper CSRF protection and session handling.
 *
 * Security Features:
 * - withCredentials: true - Sends session cookies with every request
 * - CSRF token automatic injection for POST/PUT/PATCH/DELETE requests
 * - Automatic retry on CSRF token refresh
 * - Consistent error handling across all API calls
 */

import axios from "axios";
import { api_url } from "../constants";

// CSRF cookie name - must match settings.CSRF_COOKIE_NAME in Django
const CSRF_COOKIE_NAME = "sapu_csrftoken";

// ============================================================================
// Cookie Utilities
// ============================================================================

/**
 * Parse a cookie by name from document.cookie
 * @param {string} name - Cookie name to retrieve
 * @returns {string|null} Cookie value or null if not found
 */
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  
  // If we find duplicates (parts.length > 2), we prioritize the most recent/specific one.
  // This helps resolve issues when transitioning from host-only to domain-wide cookies.
  if (parts.length >= 2) {
    return parts.pop().split(";").shift();
  }
  return null;
}

/**
 * Clear a cookie by name for current domain and host.
 * This is useful for manual cleanup or handling domain transitions.
 */
export function clearCookie(name) {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  document.cookie = `${name}=; path=/; domain=${window.location.hostname}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

// ============================================================================
// Axios Instance Configuration
// ============================================================================

/**
 * Pre-configured Axios instance for API calls
 *
 * Features:
 * - Base URL set from environment
 * - Credentials included in all requests (for cookies)
 * - JSON content type by default
 * - CSRF token automatically added via interceptor
 */
const api = axios.create({
  baseURL: api_url,

  // CRITICAL: This ensures cookies are sent with EVERY request
  // Without this, session cookies and CSRF cookies won't be sent
  withCredentials: true,
});

// ============================================================================
// Request Interceptor - CSRF Token Injection
// ============================================================================

/**
 * Request Interceptor
 *
 * Automatically adds CSRF token to all mutating requests (POST, PUT, PATCH, DELETE)
 *
 * The CSRF token is read from the 'csrftoken' cookie that Django sets.
 * This cookie is NOT HttpOnly, so JavaScript can read it.
 *
 * Note: Authentication is handled via session cookies (sent automatically with
 * withCredentials: true), not via Authorization header.
 */
api.interceptors.request.use(
  (config) => {
    // Only add CSRF token for mutating requests
    const mutatingMethods = ["post", "put", "patch", "delete"];

    if (mutatingMethods.includes(config.method.toLowerCase())) {
      const csrfToken = getCookie(CSRF_COOKIE_NAME);

      if (csrfToken) {
        // Django expects the CSRF token in the X-CSRFToken header
        config.headers["X-CSRFToken"] = csrfToken;
      } else if (process.env.NODE_ENV === "development") {
        // Log warning in development - CSRF token should be present
        console.warn(
          "[API] No CSRF token found for mutating request:",
          config.url,
        );
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// ============================================================================
// Response Interceptor - Error Handling
// ============================================================================

/**
 * Response Interceptor
 *
 * Handles common error cases:
 * - CSRF token refresh on 403 errors
 * - Session expiry detection on 401 errors
 * - Network error logging
 */
api.interceptors.response.use(
  (response) => {
    // Successful response - just return it
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 403 Forbidden - might be CSRF token issue OR Session failure
    if (error.response?.status === 403) {
      const errorMessage = error.response?.data?.detail || "";

      // Check if this is actually sub-authenticated state (DRF returns 403 instead of 401 for session failures)
      if (errorMessage.includes("Authentication credentials were not provided.")) {
        window.dispatchEvent(new CustomEvent("auth:session-expired"));
        return Promise.reject(error);
      }

      // Check if this is a CSRF verification failure
      if (errorMessage.includes("CSRF") && !originalRequest._csrfRetry) {
        // Mark this request to prevent infinite retry loop
        originalRequest._csrfRetry = true;

        try {
          // Fetch a fresh CSRF token
          // Mark this request with _csrfRetry: true to prevent infinite loops if this request fails
          await api.get("/api/csrf/", { _csrfRetry: true });

          // Retry the original request with the new CSRF token
          const newCsrfToken = getCookie(CSRF_COOKIE_NAME);
          if (newCsrfToken) {
            originalRequest.headers["X-CSRFToken"] = newCsrfToken;
          }

          return api(originalRequest);
        } catch (csrfError) {
          console.error("[API] Failed to refresh CSRF token:", csrfError);
          // Fall through to normal error handling
        }
      }
    }

    // Handle 401 Unauthorized - session has expired
    if (error.response?.status === 401) {
      // Dispatch a custom event that components can listen to
      window.dispatchEvent(new CustomEvent("auth:session-expired"));
    }

    // Handle network errors
    if (!error.response) {
      console.error("[API] Network error:", error.message);
    }

    return Promise.reject(error);
  },
);

/**
 * Initialize CSRF Token
 *
 * Call this once when the app loads to ensure we have a CSRF token
 * before making any mutating requests.
 *
 * @returns {Promise<boolean>} True if CSRF token was successfully fetched
 */
export async function initializeCsrf() {
  try {
    // Clear old host-only cookies if they exist to prevent ambiguity
    const value = `; ${document.cookie}`;
    if (value.split(`; ${CSRF_COOKIE_NAME}=`).length > 2) {
      console.log("[API] Multiple CSRF cookies detected, cleaning up...");
      clearCookie(CSRF_COOKIE_NAME);
    }
    await api.get("/api/csrf/");
    return true;
  } catch (error) {
    console.error("[API] Failed to initialize CSRF token:", error);
    return false;
  }
}

export default api;
